import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Search, Send, Trash2 } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import { postingApiService, type VendorSearchResult } from '@/lib/api/posting.service';
import type { ServiceRequest, ServiceRequestVendor } from '@om/shared-types';

export const ServiceRequestPostPage: React.FC = () => {
  const { t } = useTranslation(['services']);
  const navigate = useNavigate();
  const { celebrationId, serviceId, requestId } = useParams<{ celebrationId: string; serviceId: string; requestId: string }>();

  const cid = Number(celebrationId);
  const sid = Number(serviceId);
  const rid = Number(requestId);

  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [vendors, setVendors] = useState<ServiceRequestVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectionMessage, setSelectionMessage] = useState<string | null>(null);

  const [vendorQuery, setVendorQuery] = useState('');
  const [vendorResults, setVendorResults] = useState<VendorSearchResult[]>([]);
  const [searchingVendors, setSearchingVendors] = useState(false);
  const [addingVendorId, setAddingVendorId] = useState<number | null>(null);
  const [removingVendorId, setRemovingVendorId] = useState<number | null>(null);
  const [publishMode, setPublishMode] = useState<'all' | 'selected' | null>(null);

  const loadData = useCallback(async () => {
    if (!Number.isFinite(cid) || !Number.isFinite(rid)) {
      setError('Invalid route parameters');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setActionError(null);
    try {
      const [req, reqVendors] = await Promise.all([
        serviceRequestApiService.getRequest(cid, rid),
        serviceRequestApiService.listVendors(cid, rid),
      ]);
      setRequest(req);
      setVendors(reqVendors);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load post page');
    } finally {
      setLoading(false);
    }
  }, [cid, rid]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const selectedVendorIds = useMemo(() => new Set(vendors.map((v) => v.vendor_id)), [vendors]);

  const handleSearchVendors = useCallback(async () => {
    if (!request?.service_category) {
      setActionError('Service category is missing; cannot search vendors for this request.');
      return;
    }

    setSearchingVendors(true);
    setActionError(null);
    try {
      const found = await postingApiService.searchVendors({
        category: request.service_category,
        q: vendorQuery.trim() || undefined,
      });
      setVendorResults(found);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to search vendors');
    } finally {
      setSearchingVendors(false);
    }
  }, [request?.service_category, vendorQuery]);

  useEffect(() => {
    if (!request?.service_category) return;
    void handleSearchVendors();
  }, [request?.service_category, handleSearchVendors]);

  const handleAddVendor = async (vendorId: number) => {
    setAddingVendorId(vendorId);
    setActionError(null);
    setSuccessMessage(null);
    setSelectionMessage(null);
    try {
      const created = await serviceRequestApiService.addVendor(cid, rid, { vendor_id: vendorId });
      setVendors((prev) => {
        if (prev.some((v) => v.vendor_id === created.vendor_id)) return prev;
        return [...prev, created];
      });
      setSelectionMessage('Vendor added to request.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add vendor');
    } finally {
      setAddingVendorId(null);
    }
  };

  const handleRemoveVendor = async (vendorId: number) => {
    setRemovingVendorId(vendorId);
    setActionError(null);
    setSuccessMessage(null);
    setSelectionMessage(null);
    try {
      await serviceRequestApiService.removeVendor(cid, rid, vendorId);
      setVendors((prev) => prev.filter((v) => v.vendor_id !== vendorId));
      setSelectionMessage('Vendor removed from request.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove vendor');
    } finally {
      setRemovingVendorId(null);
    }
  };

  const handlePostToAll = async () => {
    if (!request) return;
    if (request.posting_status !== 'ready_to_post') {
      setActionError('Posting status must be ready_to_post before posting.');
      return;
    }

    const missingVendors = vendorResults.filter((v) => !selectedVendorIds.has(v.vendor_id));
    if (vendors.length === 0 && missingVendors.length === 0) {
      setActionError('No vendors available to post. Search vendors first.');
      return;
    }

    setPublishMode('all');
    setActionError(null);
    setSuccessMessage(null);
    setSelectionMessage(null);
    try {
      let addedCount = 0;
      for (const vendor of missingVendors) {
        await serviceRequestApiService.addVendor(cid, rid, { vendor_id: vendor.vendor_id });
        addedCount += 1;
      }
      if (request.status !== 'open') {
        await serviceRequestApiService.transitionStatus(cid, rid, 'open');
      }
      setSuccessMessage(
        request.status === 'open'
          ? `Added all vendors to the open request (${vendors.length + addedCount} targeted).`
          : `Request posted to all vendors and opened successfully (${vendors.length + addedCount} targeted).`,
      );
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to post to all vendors');
    } finally {
      setPublishMode(null);
    }
  };

  const handlePostToSelected = async () => {
    if (!request) return;
    if (vendors.length === 0) {
      setActionError('Select at least one vendor before posting to selected vendors.');
      return;
    }
    if (request.posting_status !== 'ready_to_post') {
      setActionError('Posting status must be ready_to_post before posting.');
      return;
    }

    setPublishMode('selected');
    setActionError(null);
    setSuccessMessage(null);
    setSelectionMessage(null);
    try {
      if (request.status !== 'open') {
        await serviceRequestApiService.transitionStatus(cid, rid, 'open');
        setSuccessMessage(`Request posted to ${vendors.length} selected vendor${vendors.length === 1 ? '' : 's'}.`);
      } else {
        setSelectionMessage(`Selected vendors are attached to this open request (${vendors.length} total).`);
      }
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to post to selected vendors');
    } finally {
      setPublishMode(null);
    }
  };

  if (loading) {
    return (
      <PageContainer width="wide">
        <div className="flex h-64 items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          {t('services:postToVendors.loading')}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-5">
      <AppBreadcrumbs
        items={[
          { label: 'Services', to: `/celebrations/${cid}/services` },
          { label: `Service #${sid}`, to: `/celebrations/${cid}/services/${sid}` },
          { label: `Request #${rid}`, to: `/celebrations/${cid}/services/${sid}/requests/${rid}` },
          { label: 'Post to Vendors' },
        ]}
      />

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{t('services:postToVendors.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('services:postToVendors.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/celebrations/${cid}/services/${sid}/requests/${rid}`)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('services:postToVendors.backToRequest')}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {actionError}
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {successMessage}
        </div>
      )}

      {!error && (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">{t('services:postToVendors.postStatus')}</h2>
            <div className="mt-3 grid gap-4 sm:grid-cols-4">
              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{t('services:postToVendors.requestStatus')}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{request?.status ?? t('services:postToVendors.unknown')}</p>
              </div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{t('services:postToVendors.postingStatus')}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{request?.posting_status ?? t('services:postToVendors.unknown')}</p>
              </div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{t('services:postToVendors.vendorsInRequest')}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{vendors.length}</p>
              </div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{t('services:postToVendors.category')}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{request?.service_category ?? t('services:postToVendors.unknown')}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => void handlePostToAll()}
                disabled={
                  publishMode !== null ||
                  !['draft', 'open'].includes(request?.status ?? '') ||
                  request?.posting_status !== 'ready_to_post'
                }
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {publishMode === 'all' ? t('services:postToVendors.posting') : t('services:postToVendors.postToAll')}
              </button>
              <p className="text-xs text-gray-500">
                {t('services:postToVendors.postRequiresStatus')}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900">{t('services:postToVendors.selectVendors')}</h2>
              <span className="text-xs text-gray-500">{t('services:postToVendors.results', { count: vendorResults.length })}</span>
            </div>

            <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{t('services:postToVendors.selectedVendors')}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {vendors.length === 0 ? t('services:postToVendors.noSelectedVendors') : t(vendors.length === 1 ? 'services:postToVendors.vendorsSelected_one' : 'services:postToVendors.vendorsSelected_other', { count: vendors.length })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void handlePostToSelected()}
                  disabled={
                    publishMode !== null ||
                    !['draft', 'open'].includes(request?.status ?? '') ||
                    request?.posting_status !== 'ready_to_post' ||
                    vendors.length === 0
                  }
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {publishMode === 'selected' ? t('services:postToVendors.posting') : t('services:postToVendors.postToSelectedVendors')}
                </button>
              </div>

              {selectionMessage && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {selectionMessage}
                </div>
              )}

              {vendors.length > 0 && (
                <div className="mt-3 space-y-2">
                  {vendors.map((vendor) => (
                    <div key={vendor.id} className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{vendor.vendor_name ?? `Vendor #${vendor.vendor_id}`}</p>
                        <p className="text-xs text-gray-500">Vendor ID: {vendor.vendor_id}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleRemoveVendor(vendor.vendor_id)}
                        disabled={removingVendorId === vendor.vendor_id}
                        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {removingVendorId === vendor.vendor_id ? t('services:postToVendors.removing') : t('services:postToVendors.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={vendorQuery}
                onChange={(e) => setVendorQuery(e.target.value)}
                placeholder={t('services:postToVendors.searchPlaceholder')}
                className="h-9 w-full max-w-md rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none"
              />
              <button
                type="button"
                onClick={() => void handleSearchVendors()}
                disabled={searchingVendors || !request?.service_category}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <Search className="h-4 w-4" />
                {searchingVendors ? t('services:postToVendors.searching') : t('services:postToVendors.search')}
              </button>
            </div>

            {vendorResults.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">{t('services:postToVendors.noVendorResults')}</p>
            ) : (
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {vendorResults.map((vendor) => {
                  const alreadyAdded = selectedVendorIds.has(vendor.vendor_id);
                  return (
                    <div key={vendor.vendor_id} className="rounded-md border border-gray-100 bg-gray-50 p-3">
                      <p className="text-sm font-medium text-gray-900">{vendor.business_name}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {vendor.city || 'Unknown city'}{vendor.state ? `, ${vendor.state}` : ''}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">Rating: {vendor.rating?.toFixed(1) ?? 'N/A'}</p>
                      <button
                        type="button"
                        onClick={() => void handleAddVendor(vendor.vendor_id)}
                        disabled={alreadyAdded || addingVendorId === vendor.vendor_id}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {alreadyAdded ? t('services:postToVendors.added') : addingVendorId === vendor.vendor_id ? t('services:postToVendors.adding') : t('services:postToVendors.addVendor')}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </PageContainer>
  );
};
